import { formatDate } from '@angular/common';
import { ElementRef, inject, Injectable } from '@angular/core';
import { FormBuilder, FormControlStatus, FormGroup } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, first, map, merge, Observable, of, switchMap, tap } from 'rxjs';

import { PartsAgreementRestResponse } from '../../../api/tur-wr/models';
import { PartsAgreementService } from '../../../api/tur-wr/services';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { translate } from '../../../shared/fn/translate';
import { GlobalStore } from '../../../shared/global.store';
import { commentCharactersValidator } from '../validator/comment-characters-validator';

const formatToGermanDate = (dateAsString: string): string => formatDate(new Date(dateAsString), 'yyyy-MM-dd', 'de-DE');

export const isDateExpired = (validityDateTo: string): boolean => {
  const validToDate = new Date(validityDateTo);
  const todaysDate = new Date();
  return validToDate < todaysDate;
};

const determineIsInDisplayOnlyMode = (isInEditMode: boolean, validityDateTo: string): boolean =>
  isDateExpired(validityDateTo) && isInEditMode;

export interface PartsAgreementsState {
  partsAgreements: PartsAgreementRestResponse[];
  formGroup: FormGroup;
  modalTemplate: ElementRef;
}

const defaultState: PartsAgreementsState = {
  partsAgreements: [],
  formGroup: undefined,
  modalTemplate: undefined,
};

@Injectable()
export class PartsAgreementsStore extends ComponentStore<PartsAgreementsState> {
  private readonly partsAgreementService = inject(PartsAgreementService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly globalStore = inject(GlobalStore);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);

  public readonly partsAgreements$ = this.select(state => state.partsAgreements);
  public readonly formGroup$ = this.select(state => state.formGroup);

  public readonly isModalOpenInDisplayOnlyMode$: Observable<boolean> = this.select(this.formGroup$, formGroup => {
    if (!formGroup) return false;
    const isInEditMode = !!formGroup.get('id').value;
    const validityDateTo = formGroup.get('validityDateTo').value;
    return determineIsInDisplayOnlyMode(isInEditMode, validityDateTo);
  });

  public readonly isFormValid$ = this.formGroup$.pipe(
    filter(Boolean),
    switchMap((form): Observable<FormControlStatus> =>
      merge(
        of(form.valid).pipe(map((isValid: boolean): FormControlStatus => (isValid ? 'VALID' : 'INVALID'))),
        form.statusChanges,
      ),
    ),
    map((status: FormControlStatus) => status === 'VALID'),
  );

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$ = this.select(
    this.isFormValid$,
    this.isModalOpenInDisplayOnlyMode$,
    (isFormValid, isModalOpenInDisplayOnlyMode) => isFormValid && !isModalOpenInDisplayOnlyMode,
  );

  public constructor() {
    super(defaultState);
  }

  public readonly loadPartsAgreements = this.effect(trigger$ =>
    trigger$.pipe(
      switchMap(() => this.partsAgreementService.retrievePartsAgreements()),
      tap((partsAgreements: PartsAgreementRestResponse[]) => this.patchState({ partsAgreements })),
    ),
  );

  public readonly editPartsAgreement = this.effect((id$: Observable<number>) =>
    id$.pipe(
      switchMap(id => this.partsAgreementService.findPartsAgreementById({ id })),
      tap((partsAgreement: PartsAgreementRestResponse) => this.initForm(partsAgreement)),
      switchMap(() => this.select(state => state.modalTemplate).pipe(first())),
      tap((elementRef: ElementRef) => this.globalStore.openModal(elementRef)),
    ),
  );

  public readonly saveOrUpdatePartsAgreement = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.globalStore.setProcessing(true)),
      switchMap(() => this.formGroup$.pipe(first())),
      map(formGroup => formGroup.value),
      switchMap(data => {
        const payload = {
          comment: data.comment,
          validityDateTo: formatToGermanDate(data.validityDateTo),
        };
        return this.partsAgreementService.updatePartsAgreement({ id: data.id, body: payload }).pipe(
          tapResponse({
            next: () => {
              const title = translate('partsAgreement.saveSuccess.title');
              const content = translate('partsAgreement.saveSuccess.content');
              this.notificationServiceWrapper.addSuccessNotification(title, content);
              this.globalStore.closeModal();
            },
            error: () => {
              const title = translate('partsAgreement.saveError.title');
              const content = translate('partsAgreement.saveError.content');
              this.notificationServiceWrapper.addErrorNotification(title, content);
              this.globalStore.closeModal();
            },
            finalize: () => {
              this.globalStore.setProcessing(false);
            },
          }),
          tap(() => this.loadPartsAgreements()),
        );
      }),
    ),
  );

  public readonly cancelPartsAgreementEditing = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.globalStore.closeModal()),
      tap(() => this.destroyForm()),
    ),
  );

  private readonly initForm = this.effect((data$: Observable<PartsAgreementRestResponse>) => {
    const createFormGroup = (data: PartsAgreementRestResponse): FormGroup => {
      const isInEditMode = !!data.id;
      const isInDisplayOnlyMode = determineIsInDisplayOnlyMode(isInEditMode, data.validityDateTo);

      return this.formBuilder.group({
        id: [data.id],
        supplierNumber: this.formBuilder.control({
          value: data.supplierNumber,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        supplierName: this.formBuilder.control({
          value: data.supplierName,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        sparePartNumber: this.formBuilder.control({
          value: data.sparePartNumber,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        technicalFactor: this.formBuilder.control({
          value: data.technicalFactor,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        orderType: this.formBuilder.control({ value: data.orderType, disabled: isInEditMode || isInDisplayOnlyMode }),
        productionDateFrom: this.formBuilder.control({
          value: data.productionDateFrom,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        productionDateTo: this.formBuilder.control({
          value: data.productionDateTo,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        modelYearFrom: this.formBuilder.control({
          value: data.modelYearFrom,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        modelYearTo: this.formBuilder.control({
          value: data.modelYearTo,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        validityDateFrom: this.formBuilder.control({
          value: data.validityDateFrom,
          disabled: isInEditMode || isInDisplayOnlyMode,
        }),
        validityDateTo: this.formBuilder.control({ value: data.validityDateTo, disabled: isInDisplayOnlyMode }),
        comment: this.formBuilder.control(
          { value: data.comment, disabled: isInDisplayOnlyMode },
          commentCharactersValidator,
        ),
      });
    };

    return data$.pipe(
      tap((data: PartsAgreementRestResponse) => {
        this.patchState({ formGroup: createFormGroup(data) });
      }),
    );
  });

  private readonly destroyForm = this.effect(trigger$ =>
    trigger$.pipe(tap(() => this.patchState({ formGroup: undefined }))),
  );
}
